import api from './api';

/** One enriched port row from GET /cms-lookups/port-lookup (non-deleted cms.ports). */
export interface PortLookupRow {
  recno: number;
  pport: string;
  pcountry: string;
  pcontinent: string;
  seaportcode: string;
  unlocode: string;
}

/** Enriched cms.ports typeahead — matches port name / UN-LOCODE / recno. Min 2 chars.
 *  The stored FK is `recno` (e.g. edi_portmap.portcode). Reusable anywhere a port is
 *  picked. */
export async function searchPorts(q: string): Promise<PortLookupRow[]> {
  const res = await api.get('/cms-lookups/port-lookup', { params: { q } });
  return res.data as PortLookupRow[];
}

/** One UN/LOCODE lookup row from GET /cms-lookups/unlocode-search (cms.un_locations,
 *  matched by diacritic-free name). `code` is the stored value = country+location
 *  (e.g. USSAV) persisted to ports.unlocode. */
export interface UnLocodeRow {
  code: string;
  name: string;
  subdivision: string;
  country: string;
  location: string;
  function: string;
}

/** UN/LOCODE typeahead for the Ports drawer — matches the UN/LOCODE name. Min 2 chars.
 *  On pick, store the row's `code` (country+location, e.g. USSAV). */
export async function searchUnLocode(q: string): Promise<UnLocodeRow[]> {
  const res = await api.get('/cms-lookups/unlocode-search', { params: { q } });
  return res.data as UnLocodeRow[];
}

/** Friendly-name resolution for a single UN/LOCODE (e.g. USSAV → "Savannah, US"). Powers the
 *  "Map Port" modal header for an EDI-supplied port that has no CMS port yet. `name` may be
 *  blank when the code doesn't resolve — the user can still map it. */
export interface UnLocodeDetail {
  code: string;
  country: string;
  location: string;
  name: string;
}

export async function getUnLocodeDetail(code: string): Promise<UnLocodeDetail> {
  const res = await api.get('/cms-lookups/unlocode-detail', { params: { code } });
  return res.data as UnLocodeDetail;
}

/** Map an EDI UN/LOCODE to a CMS port (V1 edi_savemapport): stamp the unlocode onto the
 *  chosen cms.ports row so port resolution starts naming it everywhere. Global by design —
 *  every EDI order carrying that unlocode is now mapped. */
export async function mapPort(portcode: number, unlocode: string): Promise<{ portcode: number; pport: string; unlocode: string }> {
  const res = await api.put('/cms-lookups/map-port', { portcode, unlocode });
  return res.data;
}
